import * as React from 'react';

import { CheckCircleIcon, MinusCircleIcon } from '../../icons';
import { cx } from '../../utils/cx';
import EditButton from '../Button/EditButton';

import type { CardVariant } from '../Card';

// `stone` is the neutral summary surface — `Card`/`MessagingCard`'s colored
// ramp has no neutral entry, so it lives here rather than on the shared `CardVariant`.
type MessagingCardVariant = CardVariant | 'stone';

interface IMessagingStyle {
  bg: string;
  border: string;
}

// Messaging cards use a more saturated ramp than `Card`'s notification ramp.
// Per Figma `Messaging/*` tokens: colored bg uses utility-{tone}-50/100, border uses utility-{tone}-300/500.
// `stone` is the exception — a neutral surface using utility-stone-100/200 per Figma's "Summary frame".
const MESSAGING_STYLES: Record<MessagingCardVariant, IMessagingStyle> = {
  error: { bg: 'bg-utility-error-50', border: 'border-utility-error-300' },
  warning: { bg: 'bg-utility-warning-50', border: 'border-utility-warning-300' },
  success: { bg: 'bg-utility-success-50', border: 'border-utility-success-300' },
  brand: { bg: 'bg-utility-brand-100', border: 'border-utility-brand-500' },
  mist: { bg: 'bg-utility-mist-100', border: 'border-utility-mist-500' },
  stone: { bg: 'bg-utility-stone-100', border: 'border-utility-stone-200' },
};

// Body-text color per variant. Brand intentionally uses near-black `text-primary` per Figma —
// the brand teal would compete with the strong border. Other tones use their utility-700 ramp;
// neutral `stone` uses `text-secondary`.
const TEXT_TONE: Record<MessagingCardVariant, string> = {
  error: 'text-utility-error-700',
  warning: 'text-utility-warning-700',
  success: 'text-utility-success-700',
  brand: 'text-primary',
  mist: 'text-secondary',
  stone: 'text-secondary',
};

interface IMessagingCardContext {
  variant: MessagingCardVariant;
}

const MessagingCardContext = React.createContext<IMessagingCardContext>({ variant: 'mist' });

interface IMessagingCardProps extends React.PropsWithChildren {
  variant?: MessagingCardVariant;
  className?: string;
}

const MessagingCardComponent = ({ variant = 'mist', className, children }: IMessagingCardProps) => {
  const styles = MESSAGING_STYLES[variant];
  return (
    <MessagingCardContext.Provider value={{ variant }}>
      <div className={cx('overflow-hidden rounded-xl border px-4 py-5', styles.bg, styles.border, className)}>
        {children}
      </div>
    </MessagingCardContext.Provider>
  );
};

interface ITextProps extends React.PropsWithChildren {
  icon?: React.ReactNode;
  className?: string;
}

const Text = ({ icon, className, children }: ITextProps) => {
  const { variant } = React.useContext(MessagingCardContext);
  const toneClass = TEXT_TONE[variant];

  return (
    <div className={cx('flex items-start gap-2', toneClass, className)}>
      {icon && <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center [&>svg]:size-4">{icon}</span>}
      <p className="text-sm font-medium leading-5">{children}</p>
    </div>
  );
};

interface IListItem {
  icon: React.ReactNode;
  label: React.ReactNode;
}

interface IListProps {
  items: IListItem[];
  className?: string;
}

const List = ({ items, className }: IListProps) => (
  <ul className={cx('flex flex-col gap-3', className)} role="list">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="text-secondary mt-0.5 flex size-4 shrink-0 items-center justify-center [&>svg]:size-4">
          {item.icon}
        </span>
        <span className="text-secondary text-xs font-normal leading-4">{item.label}</span>
      </li>
    ))}
  </ul>
);

interface IRequirementItem {
  label: React.ReactNode;
  met: boolean;
}

interface IRequirementsProps {
  title?: React.ReactNode;
  items: IRequirementItem[];
  columns?: 1 | 2;
  className?: string;
}

const Requirements = ({ title, items, columns = 2, className }: IRequirementsProps) => (
  <div className={cx('flex flex-col gap-5', className)}>
    {title && <span className="text-secondary text-sm font-bold">{title}</span>}
    <div className={cx('grid gap-x-0 gap-y-3', columns === 2 ? 'grid-cols-2' : 'grid-cols-1')}>
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          {item.met ? (
            <CheckCircleIcon className="text-fg-success-primary size-4 shrink-0" />
          ) : (
            <MinusCircleIcon className="text-fg-quaternary size-4 shrink-0" />
          )}
          <span className={cx('text-xs font-normal', item.met ? 'text-success-primary' : 'text-secondary')}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  </div>
);

interface ISummaryCell {
  label: React.ReactNode;
  value: React.ReactNode;
}

interface ISummaryProps {
  rows: ISummaryCell[][];
  onEdit?: () => void;
  editLabel?: string;
  className?: string;
}

const Summary = ({ rows, onEdit, editLabel = 'Edit', className }: ISummaryProps) => (
  <div className={cx('relative flex flex-col gap-3', className)}>
    {onEdit && (
      <div className="absolute right-0 top-0">
        <EditButton color="link-color" size="xs" onClick={onEdit}>
          {editLabel}
        </EditButton>
      </div>
    )}
    {rows.map((row, i) => (
      <div key={i} className="grid grid-cols-2 gap-x-3">
        {row.map((cell, j) => (
          <div key={j} className="flex flex-col gap-0.5">
            <span className="text-secondary text-xs font-normal">{cell.label}</span>
            <span className="text-primary text-sm font-medium">{cell.value}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

export type { IRequirementItem, IListItem, ISummaryCell, MessagingCardVariant };

export default Object.assign(MessagingCardComponent, {
  Text,
  List,
  Requirements,
  Summary,
});
